import { useEffect, useState } from "react";
import api, { ptIt } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { Crown, Trophy, Medal, Gift, Calendar } from "lucide-react";
import { ClassificaMese } from "@/components/ClassificaMese";

const MEDALS = ["#FFD700", "#C0C0C0", "#CD7F32"];

export default function AlboOro() {
  const [mensili, setMensili] = useState([]);
  const [stagioni, setStagioni] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/contest/storico", { params: { mesi: 12 } }).then((r) => setMensili(r.data)).catch(() => {}),
      api.get("/contest/archivio").then((r) => setStagioni(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const meseOra = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" }).slice(0, 7);
  const vincitoriMensili = mensili.filter((m) => m.vincitore && m.mese < meseOra).reverse();

  return (
    <div className="fade-up" data-testid="albo-oro-page">
      <SectionTitle sub="Tutti i vincitori del contest: premi mensili a punti e stagioni archiviate.">Albo d'oro</SectionTitle>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">{[...Array(6)].map((_, i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}</div>
      ) : (
        <div className="space-y-8 mt-4">
          <ClassificaMese />
          <div>
            <div className="flex items-center gap-2 font-display font-bold mb-3"><Trophy className="text-amber-500" size={18} /> Vincitori del mese</div>
            {vincitoriMensili.length === 0 ? (
              <div className="card-immo p-8 text-center text-sm text-[var(--muted)]">Ancora nessun vincitore mensile.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {vincitoriMensili.map((m) => (
                  <div key={m.mese} data-testid={`albo-mese-${m.mese}`} className="card-immo p-4 border-amber-200/60 bg-gradient-to-br from-amber-50/60 to-white">
                    <div className="flex items-center gap-2 text-xs text-[var(--muted)]"><Calendar size={13} /> {m.mese}</div>
                    <div className="font-display font-extrabold text-lg mt-1 flex items-center gap-2"><Crown className="text-amber-500" size={18} /> {m.vincitore.nome}</div>
                    <div className="mono text-amber-600 font-bold text-sm mt-0.5">{ptIt(m.vincitore.punti)} punti</div>
                    {m.classifica?.length > 1 && (
                      <div className="mt-2 pt-2 border-t border-[var(--border)] space-y-1">
                        {m.classifica.slice(1, 3).map((c, i) => (
                          <div key={c.agent_id} className="flex items-center justify-between text-xs">
                            <span className="inline-flex items-center gap-1.5"><Medal size={12} style={{ color: MEDALS[i + 1] }} /> {c.nome}</span>
                            <span className="mono text-[var(--muted)]">{ptIt(c.punti)} pt</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 font-display font-bold mb-3"><Medal className="text-[#FC82ED]" size={18} /> Stagioni archiviate</div>
            {stagioni.length === 0 ? (
              <div className="card-immo p-8 text-center text-sm text-[var(--muted)]">Nessuna stagione archiviata finora.</div>
            ) : (
              <div className="space-y-4">
                {stagioni.map((s) => (
                  <div key={s.id} data-testid={`albo-stagione-${s.id}`} className="card-immo p-5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="text-xs text-[var(--muted)] inline-flex items-center gap-1.5"><Calendar size={13} /> {s.season_start || "inizio"} → {s.season_end}</div>
                      <div className="text-xs text-[var(--muted)]">Archiviata da {s.by}</div>
                    </div>
                    {s.vincitore ? (
                      <div className="font-display font-extrabold text-lg mt-2 flex items-center gap-2 flex-wrap"><Crown className="text-amber-500" size={18} /> {s.vincitore.nome}
                        <span className="mono text-amber-600 text-sm">{ptIt(s.vincitore.punti_totali)} pt</span>
                        {s.premi?.primo && <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-700"><Gift size={14} /> {s.premi.primo}</span>}
                      </div>
                    ) : <div className="text-sm text-[var(--muted)] mt-2">Nessun punto assegnato in questa stagione.</div>}
                    {s.classifica?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[var(--border)] grid sm:grid-cols-3 gap-2">
                        {s.classifica.slice(0, 3).map((c, i) => (
                          <div key={c.agent_id} className="flex items-center gap-2 text-sm">
                            <Medal size={15} style={{ color: MEDALS[i] }} />
                            <span className="font-medium truncate">{c.nome}</span>
                            <span className="mono text-[var(--muted)] ml-auto">{ptIt(c.punti_totali)} pt</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
